import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConversationsProvider } from "./context/ConversationsContext";
import { DashboardsProvider } from "./context/DashboardsContext";
import TopNav from "./components/TopNav";
import Sidebar from "./components/Sidebar";
import ChatPage from "./pages/ChatPage";
import DashboardsPage from "./pages/DashboardsPage";
import DashboardBuilderPage from "./pages/DashboardBuilderPage";
import DashboardDetailPage from "./pages/DashboardDetailPage";
import LoginPage from "./pages/LoginPage";
import UsersPage from "./pages/UsersPage";
import RolesPage from "./pages/RolesPage";
import TableCatalogPage from "./pages/TableCatalogPage";
import DocumentsPage from "./pages/DocumentsPage";
import ConnectionsPage from "./pages/ConnectionsPage";
import ModelsPage, { ModelDetailPage } from "./pages/ModelsPage";
import AiModelsPage from "./pages/AiModelsPage";
import AgentsPage from "./pages/AgentsPage";
import OwnerPage from "./pages/OwnerPage";
import JobsPage, { JobDetailPage } from "./pages/JobsPage";

// The auth gate sits ABOVE the app shell: TopNav and Sidebar are hardcoded siblings of <Routes>, so a
// login screen cannot be just another route without rendering inside the shell (sidebar and all).
// While signed out we render LoginPage alone; the conversation/dashboard providers — which fetch
// authenticated endpoints — are mounted only once there is a user, so they never fire a doomed request.
function Shell() {
  const { user, loading } = useAuth();

  if (window.location.pathname.startsWith("/owner")) return <OwnerPage />;

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  return (
    <ConversationsProvider>
      <DashboardsProvider>
        <BrowserRouter>
          <div className="app-root">
            <TopNav />
            <div className="app-shell">
              <Sidebar />
              <Routes>
                <Route path="/" element={<ChatPage />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/dashboards" element={<DashboardsPage />} />
                <Route path="/dashboards/new" element={<DashboardBuilderPage />} />
                <Route path="/dashboards/:id" element={<DashboardDetailPage />} />
                {/* Admin-only pages. Rendered as routes only for admins; TopNav hides the links too. */}
                {user.isAdmin && <Route path="/users" element={<UsersPage />} />}
                {user.isAdmin && <Route path="/roles" element={<RolesPage />} />}
                {user.isAdmin && <Route path="/table-catalog" element={<TableCatalogPage />} />}
                {user.isAdmin && <Route path="/documents" element={<DocumentsPage />} />}
                {user.isAdmin && <Route path="/connections" element={<ConnectionsPage />} />}
                {user.isAdmin && <Route path="/models" element={<ModelsPage />} />}
                {user.isAdmin && <Route path="/models/:id" element={<ModelDetailPage />} />}
                {user.isAdmin && <Route path="/ai-models" element={<AiModelsPage />} />}
                {user.isAdmin && <Route path="/agents" element={<AgentsPage />} />}
                {user.isAdmin && <Route path="/jobs" element={<JobsPage />} />}
                {user.isAdmin && <Route path="/jobs/:id" element={<JobDetailPage />} />}
              </Routes>
            </div>
          </div>
        </BrowserRouter>
      </DashboardsProvider>
    </ConversationsProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}
